/* Bookable editor → how many seats one member booking may take: back-to-back
   slots (timetable) or courts (grid). An appointment is always one, so there
   is nothing to set. Empty follows the default the apps had before (3 slots,
   4 courts). The server enforces it (SEAT_LIMIT) and tells the apps the number
   so they never offer more; the desk is not held to it. Mirrors the API's
   utils/seatLimit.ts. */

type Mode = 'timetable' | 'appointments' | 'grid';

const DEFAULTS = { timetable: 3, grid: 4 } as const;
const MAX = 12;

export function seatLimitPayload(mode: Mode, value: string): { maxSeatsPerBooking?: number | null } {
  if (mode === 'appointments') return {};
  const n = parseInt(value, 10);
  return { maxSeatsPerBooking: value.trim() === '' || Number.isNaN(n) ? null : n };
}

export default function SeatLimitField({ mode, value, onChange }: { mode: Mode; value: string; onChange: (v: string) => void }) {
  if (mode === 'appointments') return null;
  const courts = mode === 'grid';
  const n = parseInt(value, 10);
  const invalid = value.trim() !== '' && (!Number.isInteger(Number(value)) || n < 1 || n > MAX);
  const id = 'max-seats-per-booking';
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {courts ? 'Most courts per booking' : 'Most slots in a row per booking'}
      </label>
      <input
        id={id}
        type="number"
        min={1}
        max={MAX}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={String(courts ? DEFAULTS.grid : DEFAULTS.timetable)}
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent ${invalid ? 'border-red-400' : 'border-gray-300'}`}
      />
      <p className={`text-[11px] mt-1 ${invalid ? 'text-red-600' : 'text-gray-400'}`}>
        {invalid
          ? `A whole number from 1 to ${MAX}, or empty for the default.`
          : courts
            ? `How many courts a member can take in one checkout (each for up to the max duration). Empty = ${DEFAULTS.grid}. The desk isn’t held to it.`
            : `How many back-to-back slots a member can take in one checkout — an hour in the steam room is two 30-minute slots. Empty = ${DEFAULTS.timetable}. The desk isn’t held to it.`}
      </p>
    </div>
  );
}
